import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Paper, Stack, Typography, Button,
  TextField, Autocomplete, Snackbar, Alert,
  LinearProgress, Fade, Grow, alpha, useTheme,
} from '@mui/material';
import { Link } from 'react-router-dom';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import SaveIcon from '@mui/icons-material/Save';

import { useApi } from '../../../hooks/useApi';

function Create() {
  const jApi = useApi();
  const theme = useTheme();
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  const [selectedAccount, setSelectedAccount] = useState(null);
  const [customer, setCustomer] = useState('');
  const [autotaskId, setAutotaskId] = useState('');
  const [accountManager, setAccountManager] = useState('');
  const [accountManagerEmail, setAccountManagerEmail] = useState('');
  const [vcio, setVcio] = useState('');
  const [vcioEmail, setVcioEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [cadenceMonths, setCadenceMonths] = useState(3);

  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Load accounts for the picker
  useEffect(() => {
    (async () => {
      try {
        setAccountsLoading(true);
        const resp = await jApi.request('GET', 'cma/account');
        const list = Array.isArray(resp.data) ? resp.data : [];
        list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setAccounts(list);
      } catch (ex) {
        setToast({ severity: 'error', text: ex?.toString?.() || 'Failed to load accounts' });
      } finally {
        setAccountsLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  // When the account changes, prefill customer/autotaskId from the account record
  useEffect(() => {
    if (!selectedAccount) return;
    if (!customer)   setCustomer(selectedAccount.name || '');
    if (!autotaskId) setAutotaskId(selectedAccount.autotask_id || '');
    // eslint-disable-next-line
  }, [selectedAccount]);

  const canSave =
    selectedAccount && customer && autotaskId && accountManager && targetDate;

  const handleSave = async () => {
    try {
      setSaving(true);
      const body = {
        account:                selectedAccount.code,
        customer,
        autotask_id:            parseInt(autotaskId, 10),
        account_manager:        accountManager,
        account_manager_email:  accountManagerEmail || '',
        vcio:                   vcio || '',
        vcio_email:             vcioEmail || '',
        primary_contact_name:   contactName  || '',
        primary_contact_email:  contactEmail || '',
        primary_contact_phone:  contactPhone || '',
        target_date:            targetDate,
        cadence_months:         parseInt(cadenceMonths || 3, 10),
        active:                 true,
      };
      const resp = await jApi.request('POST', 'cma/qbrprogram', body);
      setToast({ severity: 'success', text: `Enrolled ${resp.data.customer}` });
      setTimeout(() => navigate(`/qbr/program/${resp.data.id}`), 600);
    } catch (ex) {
      const msg = ex?.response?.data?.error?.message || ex?.toString?.() || 'Failed to enroll customer';
      setToast({ severity: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  const headerGradient = {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    p: 2,
    position: 'relative',
    overflow: 'hidden',
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md">
        <Fade in timeout={500}>
          <Box sx={{ pt: 3, pb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}
              sx={{ display: 'inline-flex', px: 2, py: 1, borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.6),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <HomeRoundedIcon sx={{ fontSize: 18, color: theme.palette.secondary.main, mr: 0.5 }}/>
                <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>Home</Typography>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Link to="/qbr" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>QBR</Typography>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Enroll customer</Typography>
            </Stack>
          </Box>
        </Fade>

        <Grow in timeout={700}>
          <Box sx={headerGradient}>
            <Typography variant="h5" sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              Enroll a customer
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pick the account, set the target date, and we'll auto-create the cadence.
            </Typography>
            {saving && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}/>}
          </Box>
        </Grow>

        <Paper sx={{ p: 3, mt: 2 }}>
          <Stack spacing={2}>
            <Autocomplete
              options={accounts}
              loading={accountsLoading}
              value={selectedAccount}
              onChange={(_, v) => setSelectedAccount(v)}
              getOptionLabel={opt => opt ? `${opt.name} (${opt.code})` : ''}
              isOptionEqualToValue={(a, b) => a?.code === b?.code}
              renderInput={(params) => (
                <TextField {...params} label="Account" required/>
              )}
            />

            <TextField label="Customer display name"
              required value={customer} onChange={e => setCustomer(e.target.value)}/>

            <TextField label="Autotask Company ID"
              required type="number" value={autotaskId}
              onChange={e => setAutotaskId(e.target.value)}/>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Account Manager"
                required value={accountManager}
                onChange={e => setAccountManager(e.target.value)} sx={{ flex: 1 }}/>
              <TextField label="Account Manager Email"
                type="email" value={accountManagerEmail}
                onChange={e => setAccountManagerEmail(e.target.value)} sx={{ flex: 1.5 }}/>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="vCIO"
                value={vcio}
                onChange={e => setVcio(e.target.value)} sx={{ flex: 1 }}/>
              <TextField label="vCIO Email"
                type="email" value={vcioEmail}
                onChange={e => setVcioEmail(e.target.value)} sx={{ flex: 1.5 }}/>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Target date (first QBR)"
                required type="date" value={targetDate}
                onChange={e => setTargetDate(e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}/>
              <TextField label="Cadence (months)"
                type="number" value={cadenceMonths}
                onChange={e => setCadenceMonths(e.target.value)}
                inputProps={{ min: 1 }} sx={{ flex: 1 }}/>
            </Stack>

            <Typography variant="overline" color="text.secondary">Primary contact (optional)</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Name"  value={contactName}  onChange={e => setContactName(e.target.value)}  sx={{ flex: 1 }}/>
              <TextField label="Email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} sx={{ flex: 1.5 }}/>
              <TextField label="Phone" value={contactPhone} onChange={e => setContactPhone(e.target.value)} sx={{ flex: 1 }}/>
            </Stack>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="text" onClick={() => navigate('/qbr')} disabled={saving}>Cancel</Button>
              <Button variant="contained" startIcon={<SaveIcon/>}
                onClick={handleSave} disabled={!canSave || saving}>
                {saving ? 'Saving…' : 'Enroll'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Container>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast.text}</Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

export default Create;
